"use client";

import { useRef, useEffect, useState } from "react";
import { motion, useAnimation } from "framer-motion";

const ANIMATION_CONFIGS = {
  contentVariants: {
    hidden: { opacity: 0, y: 60 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 120,
        damping: 12,
        duration: 0.3,
      },
    },
  },
  numberVariants: {
    hidden: { opacity: 0, y: 60 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 120,
        damping: 12,
        duration: 0.3,
        delay: 0.02,
      },
    },
  },
  containerVariants: {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.1 } },
  },
};

export default function AnimatedTimeline() {
  const [scrollProgress, setScrollProgress] = useState(0);
  const timelineRef = useRef(null);
  const maxProgressRef = useRef(0);

  useEffect(() => {
    const handleScroll = () => {
      if (!timelineRef.current) return;

      const windowHeight = window.innerHeight;
      const scrollTop = window.scrollY;

      const effectiveScrollRange = windowHeight * 3;
      const scrollPercentage = (scrollTop / effectiveScrollRange) * 100;
      const currentProgress = Math.min(95, scrollPercentage);

      if (currentProgress > maxProgressRef.current) {
        maxProgressRef.current = currentProgress;
        setScrollProgress(currentProgress);
      }
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll();

    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div
      ref={timelineRef}
      className="container mx-auto px-4 pt-16 md:py-4 max-w-6xl relative"
    >
      {/* Vertical line */}
      <div
        className="absolute left-[calc(25%+7px)] sm:left-1/2 w-1 bg-blue-600 origin-top z-0 -translate-x-1/2"
        style={{
          top: 0,
          height: `${scrollProgress}%`,
          transition:
            typeof window !== "undefined" && window.innerWidth >= 768
              ? "height 0.4s ease-out"
              : "height 0.7s ease-out",
        }}
      />

      {/* Timeline Sections */}
      <TimelineSection
        number="1"
        title="Hashedin"
        badges={[{ text: "500+ Employee Strength" }]}
        description="Founded Hashedin as a bootstrapped, 'Born In Cloud' company recognized by AWS. Known for product development services, worked with enterprises and unicorns. Our unique learning culture was recognized by GPTW. Built several products, including one acquired by RedisLabs."
        scrollProgress={scrollProgress}
        triggerPoint={10}
      />

      <TimelineSection
        number="2"
        title="Hashedin (Post-Acquisition by a Big 4 Firm)"
        link="https://www.prnewswire.com/in/news-releases/deloitte-consulting-completes-acquisition-of-hashedin-technologies-838855506.html"
        badges={[
          { text: "2500+ Employee Strength" },
          { text: "600+ AI Strength" },
        ]}
        description="Post acquisition, HashedIn scaled 5x to a 2,500+ member organization, becoming one of the most successful technology acquisitions by a Big 4 firm. The team played a foundational role in building and scaling large-scale AI and cloud transformation capabilities, including the GenAI Center of Excellence."
        scrollProgress={scrollProgress}
        triggerPoint={25}
      />

      <TimelineSection
        number="3"
        title="Think41"
        badges={[{ text: "70+ Employee Strength" }, { text: "250+ By 2026" }]}
        description="Developing GenAI agents for businesses."
        scrollProgress={scrollProgress}
        triggerPoint={40}
        isLast={true}
      />
    </div>
  );
}

function TimelineSection({
  number,
  title,
  link,
  badges,
  description,
  scrollProgress,
  triggerPoint,
  isLast = false,
}) {
  const controls = useAnimation();
  const [hasAnimated, setHasAnimated] = useState(false);

  useEffect(() => {
    if (scrollProgress >= triggerPoint && !hasAnimated) {
      controls.start("visible");
      setHasAnimated(true);
    }
  }, [scrollProgress, triggerPoint, controls, hasAnimated]);

  const { containerVariants, contentVariants, numberVariants } =
    ANIMATION_CONFIGS;

  return (
    <motion.div
      initial="hidden"
      animate={controls}
      variants={containerVariants}
      className="flex md:gap-8 mb-24 relative w-full"
    >
      {/* Number */}
      <motion.div
        variants={numberVariants}
        className="text-9xl font-bold flex justify-end items-start pt-4 font-[Montserrat] w-[20%] sm:w-1/2 text-transparent"
        style={{
          backgroundImage:
            "linear-gradient(180deg, #D6D7DB 0.02%, #FFF 111.61%)",
          backgroundClip: "text",
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
        }}
      >
        {number}
      </motion.div>

      {/* Timeline Dot */}
      <div className="relative flex justify-center w-[10%]">
        <motion.div
          initial={{ opacity: 0, scale: 0 }}
          animate={
            hasAnimated ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0 }
          }
          transition={{ duration: 0.3, delay: 0.02 }}
          className="absolute top-8 left-1/2 -translate-x-1/2 w-3 h-3 bg-blue-600 rounded-full z-10"
        />
      </div>

      {/* Content */}
      <div className="pt-4 text-left w-[70%] sm:w-1/2">
        <motion.h2
          variants={contentVariants}
          className="text-xl sm:text-[24px] font-montserrat font-bold text-gray-800 mb-4"
        >
          {link ? (
            <a
              href={link}
              target="_blank"
              rel="noopener noreferrer"
              className="hover:underline hover:decoration-blue-600 hover:underline-offset-4"
            >
              {title}
            </a>
          ) : (
            title
          )}
        </motion.h2>

        <motion.div
          variants={contentVariants}
          className="flex flex-wrap gap-4 mb-4"
        >
          {badges.map((badge, idx) => (
            <div key={idx} className="flex items-center gap-2">
              <div className="bg-blue-600 text-white p-1.5 rounded flex items-center justify-center">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                >
                  <path
                    d="M16.6137 14.5663L10.1279 18.159C9.74487 18.348 9.65879 18.348 9.27581 18.159L2.79067 14.5663C1.94155 14.0965 2.64842 12.8265 3.49827 13.297L9.70183 16.7321L15.9061 13.2963C16.7552 12.8257 17.4621 14.0958 16.6137 14.5656V14.5663ZM16.6137 10.9189L10.1279 14.5116C9.74487 14.7005 9.65879 14.7005 9.27581 14.5116L2.79067 10.9189C1.94155 10.4491 2.64842 9.17904 3.49827 9.64956L9.70183 13.0847L15.9061 9.64883C16.7552 9.17831 17.4621 10.4491 16.6137 10.9189ZM2.78265 7.27799C2.66873 7.21498 2.57375 7.12264 2.50757 7.01054C2.44138 6.89844 2.4064 6.77067 2.40625 6.64049C2.4061 6.5103 2.44079 6.38246 2.50672 6.27021C2.57265 6.15795 2.66742 6.06539 2.78119 6.00212L9.34657 2.34374C9.45519 2.28318 9.57748 2.25139 9.70183 2.25139C9.82619 2.25139 9.94848 2.28318 10.0571 2.34374L16.6225 6.00212C16.7362 6.06546 16.8309 6.15807 16.8968 6.27036C16.9627 6.38265 16.9973 6.51052 16.9971 6.6407C16.9968 6.77088 16.9618 6.89863 16.8955 7.01069C16.8293 7.12276 16.7342 7.21505 16.6203 7.27799L10.0498 10.9145C9.94154 10.9745 9.81979 11.006 9.696 11.006C9.57221 11.006 9.45045 10.9745 9.3422 10.9145L2.78265 7.27799Z"
                    fill="#FAFAFA"
                  />
                </svg>
              </div>
              <span className="font-medium font-roboto text-[12px] md:text-[16px]">
                {badge.text}
              </span>
            </div>
          ))}
        </motion.div>

        <motion.p
          variants={contentVariants}
          className="font-[300] text-[16px] font-roboto"
        >
          {description}
        </motion.p>
      </div>
    </motion.div>
  );
}


// working

